import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, FlatList, RefreshControl, Text, Pressable, ScrollView, LayoutAnimation, UIManager, Platform, Linking, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import { ActivityIndicator, Button, Modal, Portal } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import EmptyState from '../components/EmptyState';
import BoitierStatusBadge from '../components/BoitierStatusBadge';
import { Badge, Card, PageHeader, SearchPill, TextInput } from '../components/ui';
import { CONTENT_MAX_WIDTH, RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { RootStackParamList } from '../navigation/AppNavigator';
import { Vehicule } from '../types';
import { vehiculeService } from '../services/vehiculeService';
import api from '../services/api';
import { useAppSelector } from '../redux/hooks';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagedList } from '../hooks/usePagedList';

const GEOFLEET_FOLLOW_URL = 'https://geofleete-pro.com/devices/follow_map';

function openFollowMap(idSuivre: number) {
  Linking.openURL(`${GEOFLEET_FOLLOW_URL}/${idSuivre}`);
}

type Nav = StackNavigationProp<RootStackParamList, 'Main'>;

type VehiculeRowProps = {
  item: Vehicule;
  expanded: boolean;
  renewLimitIso: string;
  idSuivre?: number;
  /** Desktop layout: 3-column details grid and right-aligned actions. */
  wide: boolean;
  onToggle: (id: number) => void;
  onRenew: (id: number) => void;
  onOpenClient: (clientId: number) => void;
  onFollow: (idSuivre: number) => void;
  onEdit: (item: Vehicule) => void;
};

function DetailItem({ label, value, width }: { label: string; value: string; width: `${number}%` }) {
  const styles = useStyles();
  return (
    <View style={[styles.detailItem, { width }]}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailValue} selectable>{value}</Text>
    </View>
  );
}

const VehiculeRow = memo(function VehiculeRow({
  item,
  expanded,
  renewLimitIso,
  idSuivre,
  wide,
  onToggle,
  onRenew,
  onOpenClient,
  onFollow,
  onEdit,
}: VehiculeRowProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const statut = item.statut ?? 'INACTIF';
  const isActif = statut === 'ACTIF';
  const estARenouveler = !!item.dateExpirationAbonnement && item.dateExpirationAbonnement <= renewLimitIso;
  const itemWidth = wide ? '33.33%' : '50%';

  return (
    <Card
      onPress={() => onToggle(item.id)}
      style={styles.card}
      accessibilityLabel={`${item.immatriculation}, ${expanded ? 'masquer' : 'afficher'} les détails`}
    >
      <View style={styles.cardSummary}>
        <BoitierStatusBadge imei={item.imei} />
        <View style={styles.cardSummaryLeft}>
          <Text style={styles.plaque} numberOfLines={1}>{item.immatriculation}</Text>
          <Text style={styles.clientName} numberOfLines={1}>{item.client?.nomRaisonSociale ?? '—'}</Text>
        </View>
        <View style={styles.cardSummaryRight}>
          {estARenouveler ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Renouveler l'abonnement"
              onPress={(e: any) => {
                e?.stopPropagation?.();
                onRenew(item.id);
              }}
              style={(state) => [
                styles.renewPill,
                (state as { hovered?: boolean }).hovered || state.pressed ? styles.renewPillHover : null,
              ]}
            >
              <MaterialCommunityIcons name="autorenew" size={14} color={colors.warning} />
              <Text style={styles.renewText}>Renouveler</Text>
            </Pressable>
          ) : (
            <Badge label={isActif ? 'Actif' : 'Inactif'} tone={isActif ? 'success' : 'danger'} dot />
          )}
          <View style={styles.chevron}>
            <MaterialCommunityIcons
              name={expanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              color={colors.textSecondary}
            />
          </View>
        </View>
      </View>

      {expanded && (
        <View style={styles.cardDetails}>
          <View style={styles.detailGrid}>
            <DetailItem label="Marque" value={item.marque || '-'} width={itemWidth} />
            <DetailItem label="Modèle" value={item.modele || '-'} width={itemWidth} />
            <DetailItem label="IMEI" value={item.imei || '-'} width={itemWidth} />
            <DetailItem label="SIM" value={item.numTelSim || '-'} width={itemWidth} />
            <DetailItem label="Date de création" value={formatDateTimeFr(item.dateCreation)} width={itemWidth} />
            <DetailItem label="Expiration abonnement" value={formatDateFr(item.dateExpirationAbonnement)} width={itemWidth} />
          </View>
          <View style={[styles.cardActions, wide && styles.cardActionsWide]}>
            {item.client?.id && (
              <Button
                mode="outlined"
                compact
                icon="account-outline"
                textColor={colors.textPrimary}
                onPress={(e: any) => {
                  e?.stopPropagation?.();
                  onOpenClient(item.client!.id);
                }}
                style={[styles.actionBtn, !wide && styles.actionBtnStretch]}
              >
                Voir client
              </Button>
            )}
            {item.imei ? (
              <Button
                mode="outlined"
                compact
                icon="crosshairs-gps"
                textColor={colors.textPrimary}
                disabled={!idSuivre}
                onPress={(e: any) => {
                  e?.stopPropagation?.();
                  if (idSuivre) onFollow(idSuivre);
                }}
                style={[styles.actionBtn, !wide && styles.actionBtnStretch]}
              >
                Suivre
              </Button>
            ) : null}
            <Button
              mode="outlined"
              compact
              icon="pencil-outline"
              textColor={colors.textPrimary}
              onPress={(e: any) => {
                e?.stopPropagation?.();
                onEdit(item);
              }}
              style={[styles.actionBtn, !wide && styles.actionBtnStretch]}
            >
              Modifier
            </Button>
          </View>
        </View>
      )}
    </Card>
  );
});

function formatDateTimeFr(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('fr-FR')} ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatDateFr(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-FR');
}

export default function ParkVehicules() {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, gutter, width } = useLayout();
  const navigation = useNavigation<Nav>();
  const boitierByImei = useAppSelector((state) => state.boitierStatus.byImei);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const searchQuery = search.trim() ? debouncedSearch.trim() : '';
  const listRef = useRef<FlatList<Vehicule>>(null);
  const canLoadMoreRef = useRef(false);

  const fetchPage = useCallback(
    (page: number, size: number) => vehiculeService.getPage({ page, size, q: searchQuery }),
    [searchQuery],
  );
  const {
    items: vehicules,
    total,
    loading,
    loadingMore,
    hasMore,
    refresh,
    loadMore,
    replaceItem,
    epoch,
    error,
  } = usePagedList(fetchPage, searchQuery);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVehicule, setEditingVehicule] = useState<Vehicule | null>(null);
  const [editMarque, setEditMarque] = useState('');
  const [editModele, setEditModele] = useState('');
  const [editPlaque, setEditPlaque] = useState('');
  const [editImei, setEditImei] = useState('');
  const [editTypeBoitier, setEditTypeBoitier] = useState('');
  const [editNumTelSim, setEditNumTelSim] = useState('');
  const [savingV, setSavingV] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const toggleExpand = useCallback((id: number) => {
    if (Platform.OS === 'android') {
      UIManager.setLayoutAnimationEnabledExperimental?.(true);
    }
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    canLoadMoreRef.current = false;
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [epoch]);

  const openEditModal = useCallback((v: Vehicule) => {
    setEditingVehicule(v);
    setEditMarque(v.marque ?? '');
    setEditModele(v.modele ?? '');
    setEditPlaque(v.immatriculation ?? '');
    setEditImei(v.imei ?? '');
    setEditTypeBoitier(v.typeBoitier ?? '');
    setEditNumTelSim(v.numTelSim ?? '');
    setEditError(null);
    setShowEditModal(true);
  }, []);

  const onRenew = useCallback((id: number) => {
    navigation.navigate('Abonnement', { vehiculeId: id });
  }, [navigation]);

  const onOpenClient = useCallback((clientId: number) => {
    navigation.navigate('ProfilClient', { clientId });
  }, [navigation]);

  const onFollow = useCallback((idSuivre: number) => {
    openFollowMap(idSuivre);
  }, []);

  const handleEndReached = useCallback(() => {
    if (!canLoadMoreRef.current) return;
    loadMore();
  }, [loadMore]);

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (event.nativeEvent.contentOffset.y > 24) {
      canLoadMoreRef.current = true;
    }
  }, []);

  const handleSaveVehicule = async () => {
    if (!editingVehicule) return;
    setEditError(null);
    const imeiValue = editImei.trim();
    const simValue = editNumTelSim.trim();
    if (imeiValue && imeiValue.length !== 15) {
      setEditError("L'IMEI doit contenir exactement 15 chiffres.");
      return;
    }
    if (simValue && simValue.length !== 10) {
      setEditError('Le N° Tel SIM doit contenir exactement 10 chiffres.');
      return;
    }
    setSavingV(true);
    try {
      const { data } = await api.put<Vehicule>(`/api/vehicules/${editingVehicule.id}`, {
        marque: editMarque.trim(),
        modele: editModele.trim(),
        immatriculation: editPlaque.trim(),
        imei: imeiValue || undefined,
        typeBoitier: editTypeBoitier.trim() || undefined,
        numTelSim: simValue || undefined,
        client: editingVehicule.client?.id ? { id: editingVehicule.client.id } : undefined,
      });
      replaceItem(editingVehicule.id, { ...editingVehicule, ...data, client: data.client ?? editingVehicule.client });
      setShowEditModal(false);
    } catch (e: any) {
      setEditError(e?.response?.data?.erreur ?? 'Impossible de modifier le véhicule.');
    } finally {
      setSavingV(false);
    }
  };

  const renewLimitIso = useMemo(() => {
    const renewLimitDate = new Date();
    renewLimitDate.setDate(renewLimitDate.getDate() + 7);
    return renewLimitDate.toISOString().slice(0, 10);
  }, []);

  const renderVehicule = useCallback(({ item }: { item: Vehicule }) => (
    <VehiculeRow
      item={item}
      expanded={expandedIds.has(item.id)}
      renewLimitIso={renewLimitIso}
      idSuivre={item.imei ? boitierByImei[item.imei.trim()]?.idSuivre : undefined}
      wide={isDesktop}
      onToggle={toggleExpand}
      onRenew={onRenew}
      onOpenClient={onOpenClient}
      onFollow={onFollow}
      onEdit={openEditModal}
    />
  ), [boitierByImei, expandedIds, isDesktop, onFollow, onOpenClient, onRenew, openEditModal, renewLimitIso, toggleExpand]);

  const column = { paddingHorizontal: gutter };

  return (
    <View style={styles.container}>
      <View style={[styles.headerBlock, column, { paddingTop: isDesktop ? 32 : 16 }]}>
        <PageHeader
          title="Park Véhicules"
          subtitle={`${total} véhicule${total > 1 ? 's' : ''}`}
        />
        <SearchPill
          placeholder="Rechercher par IMEI, SIM ou plaque..."
          value={search}
          onChangeText={setSearch}
          style={styles.search}
        />
      </View>

      {loading && vehicules.length === 0 ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : (
        <FlatList
          ref={listRef}
          style={styles.listFlex}
          data={vehicules}
          keyExtractor={(item) => String(item.id)}
          contentContainerStyle={[styles.list, column]}
          keyboardShouldPersistTaps="handled"
          initialNumToRender={8}
          maxToRenderPerBatch={8}
          windowSize={7}
          updateCellsBatchingPeriod={50}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          refreshControl={(
            <RefreshControl
              refreshing={loading && vehicules.length > 0}
              onRefresh={refresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
              progressBackgroundColor={colors.surface}
            />
          )}
          ListEmptyComponent={(
            <EmptyState
              icon="car-off"
              message={error ?? (searchQuery ? 'Aucun véhicule trouvé' : 'Aucun véhicule enregistré')}
              hint={!error && searchQuery ? 'Essayez un autre IMEI, numéro SIM ou plaque.' : undefined}
            />
          )}
          ListFooterComponent={loadingMore ? (
            <ActivityIndicator color={colors.primary} style={styles.footer} />
          ) : hasMore ? (
            <Button mode="outlined" textColor={colors.textPrimary} onPress={loadMore} style={styles.footerBtn}>
              Afficher plus
            </Button>
          ) : null}
          renderItem={renderVehicule}
        />
      )}

      <Portal>
        <Modal
          visible={showEditModal}
          onDismiss={() => setShowEditModal(false)}
          contentContainerStyle={[styles.modal, { width: Math.min(560, width - 32) }]}
        >
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <Text style={styles.modalTitle}>Modifier le Véhicule</Text>
            {editingVehicule?.client?.nomRaisonSociale ? (
              <Text style={styles.modalSubtitle}>{editingVehicule.client.nomRaisonSociale}</Text>
            ) : null}
            <View style={styles.formRow}>
              <TextInput label="Marque *" value={editMarque} onChangeText={setEditMarque} style={[styles.input, styles.formCol]} />
              <TextInput label="Modèle" value={editModele} onChangeText={setEditModele} style={[styles.input, styles.formCol]} />
            </View>
            <TextInput label="Plaque d'immatriculation *" value={editPlaque} onChangeText={setEditPlaque} style={styles.input} autoCapitalize="characters" />
            <TextInput
              label="IMEI / Boitier (15 chiffres)"
              value={editImei}
              onChangeText={(t) => setEditImei(t.replace(/\D/g, '').slice(0, 15))}
              style={styles.input}
              keyboardType="numeric"
              maxLength={15}
            />
            <View style={styles.formRow}>
              <TextInput label="Type de boitier" value={editTypeBoitier} onChangeText={setEditTypeBoitier} style={[styles.input, styles.formCol]} />
              <TextInput
                label="N° Tel SIM (10 chiffres)"
                value={editNumTelSim}
                onChangeText={(t) => setEditNumTelSim(t.replace(/\D/g, '').slice(0, 10))}
                style={[styles.input, styles.formCol]}
                keyboardType="phone-pad"
                maxLength={10}
              />
            </View>
            {editError ? (
              <View style={styles.errorBox}>
                <MaterialCommunityIcons name="alert-circle-outline" size={16} color={colors.danger} />
                <Text style={styles.errorMsg}>{editError}</Text>
              </View>
            ) : null}
            <View style={styles.modalActions}>
              <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowEditModal(false)}>Annuler</Button>
              <Button
                mode="contained"
                onPress={handleSaveVehicule}
                loading={savingV}
                disabled={savingV || !editMarque.trim() || !editPlaque.trim()}
              >
                Modifier
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  container: { flex: 1, backgroundColor: c.background },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  headerBlock: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center' },
  search: { marginBottom: 16 },
  listFlex: { flex: 1 },
  list: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center', paddingTop: 4, paddingBottom: 48 },
  footer: { marginVertical: 16 },
  footerBtn: { alignSelf: 'center', marginVertical: 12, borderRadius: RADIUS.pill, borderColor: c.border },
  card: { marginBottom: 12, paddingVertical: 16, paddingHorizontal: 16 },
  cardSummary: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  cardSummaryLeft: { flex: 1, minWidth: 0 },
  cardSummaryRight: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  plaque: { ...TYPOGRAPHY.subheading, color: c.textPrimary, letterSpacing: 0.3 },
  clientName: { fontSize: 14, color: c.textSecondary, marginTop: 2 },
  renewPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: RADIUS.pill,
    backgroundColor: c.warningSoft,
    borderWidth: 1,
    borderColor: c.warningSoft,
  },
  renewPillHover: { borderColor: c.warning },
  renewText: { fontSize: 12, fontWeight: '700', color: c.warning },
  chevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: c.surfaceAlt,
  },
  cardDetails: { borderTopWidth: 1, borderTopColor: c.divider, marginTop: 16, paddingTop: 16 },
  detailGrid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: 14 },
  detailItem: { paddingRight: 12 },
  detailLabel: { fontSize: 13, color: c.textSecondary },
  detailValue: { fontSize: 15, fontWeight: '600', color: c.textPrimary, marginTop: 2 },
  cardActions: { flexDirection: 'row', gap: 8, marginTop: 16, flexWrap: 'wrap' },
  cardActionsWide: { justifyContent: 'flex-end' },
  actionBtn: { borderRadius: RADIUS.pill, borderColor: c.border },
  actionBtnStretch: { flexGrow: 1 },
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    margin: 16,
    alignSelf: 'center',
    maxHeight: '85%',
  },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary },
  modalSubtitle: { ...TYPOGRAPHY.body, color: c.textSecondary, marginTop: 2 },
  formRow: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 12 },
  formCol: { flexGrow: 1, flexBasis: 200 },
  input: { marginTop: 12 },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: RADIUS.md,
    backgroundColor: c.dangerSoft,
  },
  errorMsg: { flex: 1, color: c.danger, fontSize: 14 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 24 },
}));
